import Button from "@/components/button/Button"
import { useAuth } from "@/components/context/AuthProvider"
import FormItemDivider from "@/components/form-item/FormItemDivider"
import { SvgIcon } from "@/components/icon"
import SimpleVideoPlayer from "@/components/simple-video-player/SimpleVideoPlayer"
import useOrganizationData from "@/hooks/useOrganizationData"
import { EASETECH, globalColors } from "@/utilities/global-variables"
import { Carousel, Menu, MenuProps, Skeleton, Tooltip } from "antd"
import SkeletonImage from "antd/es/skeleton/Image"
import Sider from "antd/es/layout/Sider"
import { ItemType } from "antd/es/menu/hooks/useItems"
import classNames from "classnames"
import { FC, useState } from "react"
import ReactMarkdown from "react-markdown"
import Typography from "../typography/Typography"

import "./GuideContent.less"

const { Paragraph } = Typography
type Props = {
  isMinimized: boolean
  onClose: () => void
  onMinimize: () => void
  guideTopics: GuideTopicEntity[]
  guideTopic: GuideTopicEntity
  setCurrent: (key: string) => void
  current: Maybe<string>
  loading: boolean
  videoAsset: Maybe<string>
  imagesSet: UploadFileEntity[]
  firstImageUrl: Maybe<string>
}
const GuideContent: FC<Props> = ({
  isMinimized,
  onClose,
  onMinimize,
  guideTopics,
  setCurrent,
  current,
  loading,
  videoAsset,
  guideTopic,
  imagesSet,
  firstImageUrl,
}) => {
  const { organization } = useAuth()
  const { organizationData } = useOrganizationData()
  const [collapsed, setIsCollapsed] = useState(false)

  const menuItems = guideTopics?.map(it => {
    return {
      key: it?.id,
      icon: (
        <Tooltip title={collapsed ? it?.attributes?.title : ""} placement={"right"}>
          <SvgIcon type={"file-play"} color={"light-blue"} />
        </Tooltip>
      ),
      label: it?.attributes?.title,
      // title: it?.attributes?.title,
    } as ItemType
  })

  const onMenuClick: MenuProps["onClick"] = e => {
    setCurrent(e.key)
  }
  const classes = classNames({
    "guide-sider-content": true,
    "collapsed": collapsed,
  })

  const wrapperClasses = classNames({
    "guide-content-wrapper": true,
    "minimized": isMinimized,
  })
  const onCollapse = () => {
    setIsCollapsed(!collapsed)
  }
  return (
    <div className={wrapperClasses}>
      <div className={"guide-actions"}>
        <Button
          btnType={"icon"}
          className={"guide-actions-btn"}
          icon={<SvgIcon type={"minimize"} />}
          onClick={onMinimize}
        />
        <Button btnType={"icon"} className={"guide-actions-btn"} icon={<SvgIcon type={"close"} />} onClick={onClose} />
      </div>

      {!isMinimized && (
        <>
          <Sider
            collapsedWidth={80}
            width={220}
            // className={'dashboard-sider'}
            collapsed={collapsed}
            theme={"light"}
            style={{
              backgroundColor:
                organization === EASETECH
                  ? globalColors?.defaultPrimaryColor
                  : organizationData?.attributes?.primaryColor || globalColors.defaultPrimaryColor,
            }}
          >
            <div className={classes}>
              <div className={"guide-collapse-header"}>
                <Button
                  className={"guide-burger-btn"}
                  btnType={"icon"}
                  icon={<SvgIcon type={"burger-icon"} onClick={onCollapse} />}
                />
              </div>

              <Menu
                items={menuItems}
                onClick={onMenuClick}
                className={"guide-menu"}
                defaultSelectedKeys={current ? [current] : []}
                selectedKeys={current ? [current] : []}
                mode={"inline"}
              />
            </div>
          </Sider>
        </>
      )}
      <div className={"main-content-block"}>
        <div className={"main-content-header"}>
          <FormItemDivider marginBottom={0} title={"User guide"} titleLevel={isMinimized ? 4 : 3} />
        </div>
        <div className={"main-content-body"}>
          {loading ? (
            <>
              <Skeleton paragraph={{ rows: 3 }} active />
              <SkeletonImage active style={{ maxHeight: 300 }} />
            </>
          ) : (
            <>
              {videoAsset ? (
                <>
                  <Paragraph weight={"w700"} size={"md"}>
                    {guideTopic?.attributes?.title}
                  </Paragraph>
                  <Paragraph>
                    <ReactMarkdown className={"markdown-block"}>{guideTopic?.attributes?.content ?? ""}</ReactMarkdown>
                  </Paragraph>
                  <SimpleVideoPlayer videoUrl={videoAsset} />
                </>
              ) : imagesSet.length === 1 ? (
                <>
                  <Paragraph weight={"w700"} size={"md"}>
                    {guideTopic?.attributes?.title}
                  </Paragraph>
                  <Paragraph>
                    <ReactMarkdown className={"markdown-block"}>{guideTopic?.attributes?.content ?? ""}</ReactMarkdown>
                  </Paragraph>
                  <div className={"single-image-wrapper"}>
                    <img
                      src={firstImageUrl ?? undefined}
                      alt={imagesSet[0]?.attributes?.name ?? "image"}
                      style={{ width: "100%", maxHeight: 400, objectFit: "contain" }}
                    />
                  </div>
                </>
              ) : imagesSet.length > 1 ? (
                <>
                  <Paragraph weight={"w700"} size={"md"}>
                    {guideTopic?.attributes?.title}
                  </Paragraph>
                  <Paragraph>
                    <ReactMarkdown className={"markdown-block"}>{guideTopic?.attributes?.content ?? ""}</ReactMarkdown>
                  </Paragraph>
                  <div className={"carousel-wrapper"}>
                    <Carousel>
                      {imagesSet.map((img, index) => (
                        <div key={index} className={"guide-slider-image-wrapper"}>
                          <img
                            src={guideTopic?.attributes?.images?.data?.[index].attributes?.url}
                            alt={img.attributes?.name ?? `image-${index}`}
                            style={{ width: "100%", objectFit: "contain" }}
                          />
                        </div>
                      ))}
                    </Carousel>
                  </div>
                </>
              ) : (
                <>
                  <Paragraph weight={"w700"} size={"md"}>
                    {guideTopic?.attributes?.title}
                  </Paragraph>
                  <Paragraph>
                    <ReactMarkdown className={"markdown-block"}>{guideTopic?.attributes?.content ?? ""}</ReactMarkdown>
                  </Paragraph>
                </>
              )}
            </>
          )}
        </div>

        <div className={"main-content-footer"}>
          {!isMinimized && <Button size={"small"} text={"Close"} onClick={onClose} />}
        </div>
      </div>
    </div>
  )
}

export default GuideContent
